import { createContext, useState, useEffect } from "react";

// 🔹 Crear contexto
export const AuthContext = createContext();

// 🔹 Provider para envolver la app
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [modulos, setModulos] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // 🔹 Preferencia de menú: "lateral" (default) o "superior".
  // Es solo una preferencia visual del navegador, no un dato sensible.
  const [menuPosition, setMenuPositionState] = useState(() => {
    try {
      return localStorage.getItem("menuPosition") === "superior" ? "superior" : "lateral";
    } catch {
      return "lateral";
    }
  });

  const setMenuPosition = (posicion) => {
    const valor = posicion === "superior" ? "superior" : "lateral";
    setMenuPositionState(valor);
    try {
      localStorage.setItem("menuPosition", valor);
    } catch {
      // localStorage no disponible; la preferencia solo dura la sesión en memoria
    }
  };

  // 🔹 Cargar perfil del usuario desde API (la sesión viaja en una cookie httpOnly)
  const cargarPerfil = async () => {
    const res = await fetch("/api/usuarios/perfil", {
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!res.ok) throw new Error("No autenticado");
    const data = await res.json();
    setUser(data);
    return data;
  };

  // 🔹 Cargar módulos del usuario desde API
  const cargarModulos = async () => {
    const res = await fetch("/api/usuarios/modulos", {
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!res.ok) throw new Error("Error obteniendo módulos");
    const data = await res.json();
    setModulos(data);
  };

  // 🔹 Al montar la app, verificar si ya existe una sesión válida (cookie httpOnly)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        await cargarPerfil();
        await cargarModulos();
        setIsAuthenticated(true);
      } catch (err) {
        setUser(null);
        setModulos([]);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // 🔹 Login: el POST /api/login ya dejó la cookie httpOnly puesta;
  // aquí solo cargamos perfil/modulos y marcamos la sesión como activa.
  const login = async () => {
    await cargarPerfil();
    await cargarModulos();
    setIsAuthenticated(true);
  };

  // 🔹 Logout: pedir al backend que borre la cookie y limpiar el estado local
  const logout = async () => {
    try {
      await fetch("/api/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch (err) {
      console.error("Error cerrando sesión:", err);
    }
    setUser(null);
    setModulos([]);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        modulos,
        isAuthenticated,
        // Se conserva `token` (booleano) por compatibilidad con componentes
        // que solo lo usaban para saber "¿hay sesión?" o para armar un header
        // Authorization que el backend ya no necesita (ahora usa la cookie).
        token: isAuthenticated,
        loading,
        login,
        logout,
        menuPosition,
        setMenuPosition,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
